/**
 * Admin → Settings (Phase D)
 *
 * Currently houses bank-transfer details and the payment reference prefix
 * that admin uses to reconcile incoming payments to delivery records.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Save, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function adminFetch(path, opts = {}) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: t ? `Bearer ${t}` : '',
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!res.ok) throw new Error((await res.text()) || `${res.status}`);
  return res.json();
}

const BLANK = {
  bank_name: '', account_holder: '', iban: '', swift: '', bank_code: '',
  notes: '', payment_reference_prefix: 'SP',
};

export default function AdminSettings() {
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await adminFetch('/api/admin/settings/bank-details');
        setForm({ ...BLANK, ...d });
      } catch (e) {
        toast.error(e.message || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await adminFetch('/api/admin/settings/bank-details', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-settings-page">
      <header>
        <p className="text-[11px] uppercase tracking-[.18em] text-[#F25C05] font-bold">Settings</p>
        <h1 className="font-display text-3xl text-[#05223D]">Bank details &amp; payments</h1>
        <p className="text-sm text-[#465B70] mt-1">These details are shown to customers in the cabinet when they need to pay.</p>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-[#465B70]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <Card>
          <CardContent className="p-6">
            <form onSubmit={save} className="space-y-4" data-testid="bank-details-form">
              <div className="flex items-center gap-2 text-[#F25C05] font-bold"><Banknote size={18} /> Bank account</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Bank name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} tid="bank-name" />
                <Field label="Account holder" value={form.account_holder} onChange={(v) => setForm({ ...form, account_holder: v })} tid="account-holder" />
                <Field label="IBAN" value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} tid="iban" />
                <Field label="SWIFT / BIC" value={form.swift} onChange={(v) => setForm({ ...form, swift: v })} tid="swift" />
                <Field label="Bank code" value={form.bank_code} onChange={(v) => setForm({ ...form, bank_code: v })} tid="bank-code" />
                <Field label="Payment ref. prefix" value={form.payment_reference_prefix} onChange={(v) => setForm({ ...form, payment_reference_prefix: v.toUpperCase().slice(0, 10) })} tid="ref-prefix" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#465B70]">Internal notes (not shown to customers)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="bank-notes" className="mt-1" />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={saving} className="h-10 rounded-xl bg-[#F25C05] hover:bg-[#D95204]" data-testid="bank-save">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save settings
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#465B70]">{label}</Label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="h-10 rounded-xl mt-1" />
    </div>
  );
}
